import { useQuery, useQueryClient } from '@tanstack/react-query';
import { compareTagNames } from './tagNames';
import { getReviewId } from 'query/review';
import { getTags, ReviewTag } from 'query/review/Tags';

type UseTagsResult = {
  isLoading: boolean;
  status: 'loading' | 'error' | 'success';
  tags: ReviewTag[];
  addTag: (tag: ReviewTag) => Promise<void>;
  refetchTags: () => Promise<void>;
};

export const tagsQueryKey = () => ['tags', getReviewId() || ''];

const STALE_TIME_MS = 10_000;

// Shared so readers' memoised work isn't redone on every render while loading
const NO_TAGS: ReviewTag[] = [];

// Approximates the server's citext ordering so the tag doesn't jump on refetch. Pinned to
// 'en' because some browser locales sort differently (e.g. Danish puts "aa" after "z")
const insertByName = (tags: ReviewTag[], tag: ReviewTag) => {
  const index = tags.findIndex(
    ({ name }) => compareTagNames(name, tag.name) > 0
  );
  return index === -1
    ? [...tags, tag]
    : [...tags.slice(0, index), tag, ...tags.slice(index)];
};

export function useTags(): UseTagsResult {
  const queryClient = useQueryClient();
  const queryKey = tagsQueryKey();

  const { isLoading, status, data } = useQuery({
    queryKey,
    queryFn: getTags,
    staleTime: STALE_TIME_MS,
  });

  return {
    isLoading,
    // A failed background refetch keeps the loaded tags, so it isn't an error to readers
    status: status === 'error' && data ? 'success' : status,
    tags: data ?? NO_TAGS,
    addTag: async (tag) => {
      const hasLoaded = queryClient.getQueryData(queryKey) !== undefined;
      // A fetch started before the tag was created could settle late and drop it
      const cancel = queryClient.cancelQueries({ queryKey });
      // With nothing loaded yet, [tag] would pass for the full list
      queryClient.setQueryData<ReviewTag[] | undefined>(
        queryKey,
        (prev) => prev && insertByName(prev, tag)
      );
      // Cancelling the first load discards the existing tags, so fetch them again
      if (!hasLoaded) void queryClient.invalidateQueries({ queryKey });
      return cancel;
    },
    refetchTags: () => queryClient.invalidateQueries({ queryKey }),
  };
}
